import { createFinderRoutes } from "./finder-route";
import {
  decodeFinderParams,
  replaceFinderParams,
  finderParameterFamily,
  ownsFinderParameter,
} from "./finder-url-codec";

const transientLocationKey = "spFinderTransientLocation";

function comparableUrl(value) {
  const url = new URL(value, window.location.href);
  url.searchParams.sort();
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Owns finder query updates and restoration while leaving unrelated URL state intact. */
export function createFinderHistory(options) {
  const routes = createFinderRoutes(options);
  const parameterNames = new Set(options.parameterNames?.map(finderParameterFamily));
  const configuredOwnership = options.parameterNames !== undefined;

  function current() {
    const url = new URL(window.location.href);

    const decoded = decodeFinderParams(url.search);
    const params = Object.fromEntries(
      Object.entries(decoded).filter(
        ([name]) => !configuredOwnership || [...parameterNames].some((family) => ownsFinderParameter(family, name)),
      ),
    );
    return {
      route: routes.match(url.pathname),
      params,
      ...(window.history.state?.[transientLocationKey] === true ? { transientLocation: true } : {}),
    };
  }

  function destination(route, params) {
    const path = routes.pathFor(route);

    if (!path) {
      return;
    }

    const url = new URL(window.location.href);
    url.pathname = path;
    const ownedNames = new Set([...parameterNames, ...Object.keys(params).map(finderParameterFamily)]);
    const cleared = Object.fromEntries([...ownedNames].map((name) => [name, null]));
    replaceFinderParams(url.searchParams, { ...cleared, ...params });

    return `${url.pathname}${url.search}${url.hash}`;
  }

  function commit(route, params, { user = false, replace = false, transientLocation } = {}) {
    const next = destination(route, params);
    if (!next) {
      return;
    }
    for (const name of Object.keys(params)) {
      parameterNames.add(finderParameterFamily(name));
    }
    const changedProvenance =
      transientLocation !== undefined && Boolean(window.history.state?.[transientLocationKey]) !== transientLocation;
    let nextState = window.history.state;
    if (changedProvenance) {
      nextState = { ...nextState };
      if (transientLocation) {
        nextState[transientLocationKey] = true;
      } else {
        delete nextState[transientLocationKey];
      }
    }
    if (comparableUrl(next) !== comparableUrl(window.location.href)) {
      window.history[user && !replace ? "pushState" : "replaceState"](nextState, "", next);
    } else if (changedProvenance) {
      window.history.replaceState(nextState, "", window.location.href);
    }
  }

  function listen(listener, { signal } = {}) {
    function restore() {
      listener(current());
    }

    window.addEventListener("popstate", restore, { signal });

    return () => window.removeEventListener("popstate", restore);
  }

  return Object.freeze({ commit, current, destination, listen });
}
